import { useEffect, useRef, useState } from "react";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";

/**
 * Phase 5 (F-M9) — Live-View für einen Orchestrator-Run.
 *
 * Öffnet eine WebSocket-Connection zu `/ws/orchestrator/runs/:runId` und
 * rendert die 13 Gates mit Status-Pillen + Spinner für running. Bei Done /
 * Error closing-Frame schließt die UI sauber, der Spinner geht weg.
 *
 * Mobile-Layout: 1-spaltig, Touch-freundliche Pillen, kein horizontaler
 * Scroll. Desktop: gleicher 1-spaltiger Layout mit mehr padding.
 */

type GateStatus = "pending" | "running" | "passed" | "failed" | "skipped";

interface Gate {
  id: string;
  name: string;
  status: GateStatus;
  message?: string;
  startedAt?: string;
  finishedAt?: string;
}

interface RunSnapshot {
  runId: string;
  status: "queued" | "running" | "passed" | "failed" | "cancelled";
  startedAt?: string;
  finishedAt?: string;
  gates: Gate[];
}

type ConnectionState = "connecting" | "streaming" | "done" | "error" | "closed";

const STATUS_LABEL: Record<GateStatus, string> = {
  pending: "Wartet",
  running: "Läuft",
  passed: "OK",
  failed: "Fehler",
  skipped: "Übersprungen",
};

const STATUS_CLASS: Record<GateStatus, string> = {
  pending: "bg-jarvis-bg border border-jarvis-border text-jarvis-muted",
  running: "bg-blue-500/20 text-blue-300 border border-blue-500/40",
  passed: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40",
  failed: "bg-rose-500/20 text-rose-300 border border-rose-500/40",
  skipped: "bg-jarvis-bg text-jarvis-muted border border-jarvis-border",
};

function orchestratorRunErrorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function orchestratorRunErrorCopy(error: unknown): string {
  const text = orchestratorRunErrorText(error);
  if (/invalid.*run|run.*not found|orchestrator.*not found|run_state|stale.*run|ungültige runid/i.test(text)) {
    return "Orchestrator-Run ist nicht mehr aktuell oder die runId wurde nicht akzeptiert. Bitte Run-ID prüfen und Live-Stream neu öffnen.";
  }
  if (/origin.*not allowed|origin nicht erlaubt|forbidden|4403/i.test(text)) {
    return "Orchestrator-Live-Stream wurde vom Server-Gate abgelehnt. Bitte Session und erlaubte Oberfläche prüfen.";
  }
  if (/too many|zu viele|connection cap|4429|rate limit/i.test(text)) {
    return "Zu viele Orchestrator-Live-Verbindungen. Bitte einen Stream schließen und erneut öffnen.";
  }
  if (/websocket.*(failed|fehler|error)|verbindungsfehler/i.test(text)) {
    return "Orchestrator-Live-Stream konnte nicht belastbar verbunden werden. Bitte Session prüfen und den Stream erneut öffnen.";
  }
  return ownerFacingGlobalError(text);
}

interface Props {
  runId: string;
  /** Legacy-Fallback fuer lokale/dev Clients. Owner-UI nutzt die HttpOnly-
   *  Cookie-Session; API-Credentials sollen nicht im Browser liegen. */
  apiToken?: string;
  onClose?: () => void;
}

export function OrchestratorRunView({ runId, apiToken, onClose }: Props): JSX.Element {
  const [snapshot, setSnapshot] = useState<RunSnapshot | null>(null);
  const [state, setState] = useState<ConnectionState>("connecting");
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    // Browser → Server gleiche Origin (Vite-Proxy macht /ws/* durch).
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/ws/orchestrator/runs/${encodeURIComponent(runId)}`;
    // Same-origin WebSocket sendet die HttpOnly-Session-Cookie automatisch.
    // Nur explizite Dev-/Legacy-Clients duerfen optional ein Bearer-Subprotocol nutzen.
    const protocols = [apiToken ? `bearer.${apiToken}` : null].filter((value): value is string => Boolean(value));
    const ws = new WebSocket(url, protocols);
    wsRef.current = ws;

    ws.onopen = (): void => {
      // Beim erfolgreichen Open einen evtl. von vorigem Mount übrig
      // gebliebenen Error zurücksetzen — React.StrictMode mountet Effects
      // doppelt, der erste WS wird durch Cleanup geschlossen und löst
      // onerror aus. Ohne diesen Reset bleibt das Error-Banner sichtbar,
      // obwohl der zweite WS streamt.
      setError(null);
      setState("streaming");
    };
    ws.onmessage = (ev): void => {
      try {
        const parsed = JSON.parse(String(ev.data)) as
          | { type: "snapshot"; data: RunSnapshot }
          | { type: "end"; reason: string }
          | { type: "error"; message: string };
        if (parsed.type === "snapshot") {
          setSnapshot(parsed.data);
          if (
            parsed.data.status === "passed" ||
            parsed.data.status === "failed" ||
            parsed.data.status === "cancelled"
          ) {
            setState("done");
          }
        } else if (parsed.type === "end") {
          setState("done");
        } else if (parsed.type === "error") {
          setError(orchestratorRunErrorCopy(parsed.message));
          setState("error");
        }
      } catch {
        /* malformed frame ignorieren */
      }
    };
    ws.onerror = (): void => {
      setError(orchestratorRunErrorCopy("WebSocket-Verbindungsfehler"));
      setState("error");
    };
    ws.onclose = (ev): void => {
      // Close-Codes aus dem WS-Endpoint:
      //   4400 = invalid runId
      //   4401 = unauthorized
      //   4403 = origin not allowed
      //   4429 = connection cap
      if (ev.code === 4401) setError(orchestratorRunErrorCopy("Owner-Session fehlt oder ist abgelaufen. Bitte neu anmelden und den Orchestrator-Live-Stream erneut öffnen. Endpoint: /ws/orchestrator/runs."));
      else if (ev.code === 4400) setError(orchestratorRunErrorCopy("invalid runId"));
      else if (ev.code === 4403) setError(orchestratorRunErrorCopy("Origin nicht erlaubt"));
      else if (ev.code === 4429) setError(orchestratorRunErrorCopy("Zu viele Verbindungen"));
      setState((s) => (s === "streaming" ? "closed" : s));
    };

    return (): void => {
      try {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      } catch {
        /* ignore */
      }
      wsRef.current = null;
    };
  }, [runId, apiToken]);

  const stateBadge = ((): { label: string; cls: string } => {
    if (state === "connecting") return { label: "Verbinde…", cls: "bg-blue-500/20 text-blue-300" };
    if (state === "streaming") return { label: "Live", cls: "bg-emerald-500/20 text-emerald-300" };
    if (state === "done") return { label: "Beendet", cls: "bg-jarvis-bg text-jarvis-muted" };
    if (state === "error") return { label: "Fehler", cls: "bg-rose-500/20 text-rose-300" };
    return { label: "Getrennt", cls: "bg-amber-500/20 text-amber-300" };
  })();

  const completed = snapshot?.gates.filter((g) => g.status === "passed" || g.status === "failed" || g.status === "skipped").length ?? 0;
  const total = snapshot?.gates.length ?? 0;

  return (
    <div className="card p-3 sm:p-4 space-y-3 sm:space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold uppercase tracking-wider text-xs text-jarvis-accent">
              Orchestrator-Run
            </h3>
            <span className="text-xs font-mono text-jarvis-muted truncate" title={runId}>
              {runId}
            </span>
            <span className={`badge ${stateBadge.cls} text-[10px] uppercase tracking-wider px-2 py-0.5 rounded`}>
              {stateBadge.label}
            </span>
          </div>
          {snapshot && (
            <div className="text-xs text-jarvis-muted mt-1">
              {completed}/{total} Gates · Status:{" "}
              <span className="text-jarvis-text">{snapshot.status}</span>
            </div>
          )}
        </div>
        {onClose && (
          <button className="btn shrink-0" onClick={onClose}>
            Schließen
          </button>
        )}
      </div>

      {error && (
        <div className="card p-3 border-rose-500/50 bg-rose-500/10 text-sm text-rose-200">
          {error}
        </div>
      )}

      {!snapshot && state === "connecting" && (
        <div className="text-sm text-jarvis-muted">Warte auf Live-Stream…</div>
      )}

      {snapshot && (
        <ul className="space-y-1.5">
          {snapshot.gates.map((g) => (
            <li
              key={g.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 px-3 py-2 rounded border border-jarvis-border bg-jarvis-bg/40"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{g.name}</div>
                {g.message && (
                  <div className="text-xs text-jarvis-muted truncate" title={g.message}>
                    {g.message}
                  </div>
                )}
              </div>
              <span
                className={`shrink-0 px-2 py-0.5 rounded text-xs uppercase tracking-wider ${STATUS_CLASS[g.status]}`}
              >
                {g.status === "running" && <span className="inline-block animate-pulse mr-1">●</span>}
                {STATUS_LABEL[g.status]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
